import { useState, useEffect, useCallback, useRef } from 'react';
import PipelineCard from './PipelineCard';
import { pipelineApi as defaultApi, describeError, nextPollDelay } from '../services/pipelineApi';

// Review & Publish: the one place the owner comes back to. Every idea that was sent
// through Generate All appears here with its progress; when one is ready, all its
// platforms are shown together for ONE approval. Nothing else to remember.
const MAX_SHOWN = 8;
const RECENT_DONE_MS = 30 * 24 * 3600 * 1000;
const ORDER = { ready: 0, running: 1, scheduling: 1, failed: 2, completed: 3 };

// Which pipelines to show, and in what order: needs-you first, then in-progress,
// then problems, then recently finished.
export function pickPipelines(rows, now = Date.now()) {
  return [...rows]
    .filter((r) => r.status !== 'completed' || now - new Date(r.updatedAt || r.finishedAt || 0).getTime() < RECENT_DONE_MS)
    .sort((a, b) => (ORDER[a.status] ?? 9) - (ORDER[b.status] ?? 9) || new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))
    .slice(0, MAX_SHOWN);
}

export default function IdeaReview({ api = defaultApi, onCountChange, onOpenCalendar = () => {}, pollMs = 4000, legacy = null, extra = null }) {
  const [items, setItems] = useState([]);           // full status objects
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [rejected, setRejected] = useState({});     // { ideaId: [platId] }
  const [busy, setBusy] = useState({});
  const [errors, setErrors] = useState({});
  const [legacyOpen, setLegacyOpen] = useState(false);   // the old screen loads only when asked for
  const alive = useRef(true);
  // Set true on every mount: React's strict mode mounts, unmounts and mounts again in development.
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const loadAll = useCallback(async () => {
    const list = await api.list();
    if (!alive.current) return;
    if (!list.ok) { setLoadError(describeError(list)); setLoading(false); return; }
    const picked = pickPipelines((list.body && list.body.pipelines) || []);
    const statuses = await Promise.all(picked.map((r) => api.status(r.ideaId)));
    if (!alive.current) return;
    setItems(statuses.filter((s) => s.ok && s.body && s.body.pipeline).map((s) => s.body.pipeline));
    setLoadError(null);
    setLoading(false);
  }, [api]);

  const refreshOne = useCallback(async (ideaId) => {
    const res = await api.status(ideaId);
    if (!alive.current || !res.ok || !res.body || !res.body.pipeline) return;
    setItems((prev) => prev.map((p) => (p.ideaId === ideaId ? res.body.pipeline : p)));
  }, [api]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Keep watching only the ideas the server is still working on by itself.
  useEffect(() => {
    const active = items.filter((p) => nextPollDelay(p.status));
    if (!active.length) return undefined;
    const timer = setInterval(() => active.forEach((p) => refreshOne(p.ideaId)), pollMs);
    return () => clearInterval(timer);
  }, [items, refreshOne, pollMs]);

  const readyCount = items.filter((p) => p.status === 'ready').length;
  useEffect(() => { if (onCountChange) onCountChange(readyCount); }, [readyCount, onCountChange]);

  const setError = (ideaId, msg) => setErrors((e) => ({ ...e, [ideaId]: msg }));
  const toggleReject = (ideaId, platId) => setRejected((r) => {
    const cur = new Set(r[ideaId] || []);
    if (cur.has(platId)) cur.delete(platId); else cur.add(platId);
    return { ...r, [ideaId]: [...cur] };
  });

  const approve = async (ideaId) => {
    setBusy((b) => ({ ...b, [ideaId]: true })); setError(ideaId, null);
    const res = await api.approve(ideaId, rejected[ideaId] || []);
    if (!res.ok) setError(ideaId, describeError(res));
    await refreshOne(ideaId);
    if (alive.current) setBusy((b) => ({ ...b, [ideaId]: false }));
  };
  const retry = async (ideaId) => {
    setBusy((b) => ({ ...b, [ideaId]: true })); setError(ideaId, null);
    const res = await api.start(ideaId);
    if (!res.ok) setError(ideaId, describeError(res));
    await refreshOne(ideaId);
    if (alive.current) setBusy((b) => ({ ...b, [ideaId]: false }));
  };

  return (
    <div>
      {/* Optional card shown first, above the pipelines (e.g. posts from SHIOS waiting for approval). */}
      {extra}

      {loadError && (
        <div role="alert" style={{ background: '#fef2f2', border: '1px solid #dc2626', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#b91c1c' }}>{'⚠'} Could not load your ideas</div>
          <div style={{ fontSize: 12, color: '#7f1d1d', marginTop: 3 }}>{loadError}</div>
          <button type="button" onClick={loadAll} style={{ marginTop: 8, padding: '5px 12px', fontSize: 12, fontWeight: 600, borderRadius: 7, border: '1px solid #fca5a5', background: '#fff', cursor: 'pointer', color: '#b91c1c' }}>Try again</button>
        </div>
      )}

      {loading && !loadError && <div style={{ fontSize: 13, color: '#64748b', padding: 12 }}>Loading{'…'}</div>}

      {!loading && !loadError && items.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 16px', color: '#475569' }}>
          <div style={{ fontSize: 40 }}>{'✨'}</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: '8px 0 4px' }}>Nothing to review right now</div>
          <div style={{ fontSize: 13, lineHeight: 1.6, maxWidth: 440, margin: '0 auto' }}>
            Pick an idea on the Repurpose page and click <strong>Generate All</strong>. It will show up here, and you will get a phone alert when it is ready for your review.
          </div>
        </div>
      )}

      {items.map((p) => (
        <PipelineCard key={p.ideaId} pipeline={p} rejected={rejected[p.ideaId] || []}
          onToggleReject={(platId) => toggleReject(p.ideaId, platId)}
          onApprove={() => approve(p.ideaId)} onRetry={() => retry(p.ideaId)} onOpenCalendar={onOpenCalendar}
          busy={!!busy[p.ideaId]} error={errors[p.ideaId] || null} />
      ))}

      {legacy && (
        <details style={{ marginTop: 18 }} open={legacyOpen} onToggle={(e) => setLegacyOpen(e.currentTarget.open)}>
          <summary style={{ cursor: 'pointer', fontSize: 13, color: '#64748b', fontWeight: 600 }}>Older finished assets from before this workflow (advanced)</summary>
          {/* Mounted only when opened: it fetches data and asks Claude for captions as soon as it loads. */}
          <div style={{ marginTop: 10 }}>{legacyOpen && legacy}</div>
        </details>
      )}
    </div>
  );
}
